/**
 * Edit Suppression Rule Dialog
 *
 * Allows editing rule criteria, name, description, and reason.
 * Note: expires_at is NOT editable (backend schema intentionally omits it).
 */

import { useState, useEffect } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateSuppressionRule } from "@/hooks/useSuppressionRules";
import type { SuppressionRule } from "@/types/suppressionRule";
import type { UpdateSuppressionRuleData } from "@/services/suppressionRulesService";

interface EditSuppressionRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: SuppressionRule;
}

const SEVERITY_OPTIONS = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];

export function EditSuppressionRuleDialog({
  open,
  onOpenChange,
  rule,
}: EditSuppressionRuleDialogProps) {
  const updateMutation = useUpdateSuppressionRule();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [matchIssueType, setMatchIssueType] = useState("");
  const [matchTitlePattern, setMatchTitlePattern] = useState("");
  const [matchSeverity, setMatchSeverity] = useState("");
  const [matchScanner, setMatchScanner] = useState("");
  const [reason, setReason] = useState("");

  // Populate form when dialog opens
  useEffect(() => {
    if (open) {
      setName(rule.name);
      setDescription(rule.description ?? "");
      setMatchIssueType(rule.match_issue_type ?? "");
      setMatchTitlePattern(rule.match_title_pattern ?? "");
      setMatchSeverity(rule.match_severity ?? "");
      setMatchScanner(rule.match_scanner ?? "");
      setReason(rule.reason ?? "");
      updateMutation.reset();
    }
  }, [open, rule.id]);

  const hasAnyCriteria =
    matchIssueType.trim() !== "" ||
    matchTitlePattern.trim() !== "" ||
    matchSeverity !== "" ||
    matchScanner.trim() !== "";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !hasAnyCriteria) return;

    // Only send changed fields
    const updates: UpdateSuppressionRuleData = {};
    if (name.trim() !== rule.name) updates.name = name.trim();
    const newDesc = description.trim() || null;
    if (newDesc !== rule.description) updates.description = newDesc;
    const newType = matchIssueType.trim() || null;
    if (newType !== rule.match_issue_type) updates.match_issue_type = newType;
    const newPattern = matchTitlePattern.trim() || null;
    if (newPattern !== rule.match_title_pattern) updates.match_title_pattern = newPattern;
    const newSeverity = matchSeverity || null;
    if (newSeverity !== rule.match_severity) updates.match_severity = newSeverity;
    const newScanner = matchScanner.trim() || null;
    if (newScanner !== rule.match_scanner) updates.match_scanner = newScanner;
    const newReason = reason.trim() || null;
    if (newReason !== rule.reason) updates.reason = newReason;

    // Skip API call if nothing changed
    if (Object.keys(updates).length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      await updateMutation.mutateAsync({ ruleId: rule.id, data: updates });
      onOpenChange(false);
    } catch {
      // Error handled by mutation state
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Edit Suppression Rule</DialogTitle>
          <DialogDescription>
            Modify the rule criteria or metadata. At least one match criterion is required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 min-h-0 py-2 px-1">
          {updateMutation.isError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : "Failed to update rule"}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-rule-name">
              Rule Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-rule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional description"
            />
          </div>

          <div className="space-y-3">
            <Label>Match Criteria</Label>
            <div className="space-y-3 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="edit-issue-type" className="text-xs text-muted-foreground">
                  Issue Type
                </Label>
                <Input
                  id="edit-issue-type"
                  value={matchIssueType}
                  onChange={(e) => setMatchIssueType(e.target.value)}
                  placeholder="e.g. PII_DETECTION"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-severity" className="text-xs text-muted-foreground">
                  Severity
                </Label>
                <Select
                  value={matchSeverity || "__any__"}
                  onValueChange={(v) => setMatchSeverity(v === "__any__" ? "" : v)}
                >
                  <SelectTrigger id="edit-severity">
                    <SelectValue placeholder="Any severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any severity</SelectItem>
                    {SEVERITY_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-scanner" className="text-xs text-muted-foreground">
                  Scanner
                </Label>
                <Input
                  id="edit-scanner"
                  value={matchScanner}
                  onChange={(e) => setMatchScanner(e.target.value)}
                  placeholder="e.g. prompt_injection"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-title-pattern" className="text-xs text-muted-foreground">
                  Title Pattern (glob)
                </Label>
                <Input
                  id="edit-title-pattern"
                  value={matchTitlePattern}
                  onChange={(e) => setMatchTitlePattern(e.target.value)}
                  placeholder="e.g. *Injection*"
                />
                <p className="text-xs text-muted-foreground">
                  Use * for wildcard matching
                </p>
              </div>
            </div>

            {!hasAnyCriteria && (
              <p className="text-xs text-destructive">
                At least one match criterion is required.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-reason">Reason</Label>
            <Textarea
              id="edit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why is this being suppressed?"
            />
          </div>

          <DialogFooter className="flex-shrink-0 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || !hasAnyCriteria || updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
